// Purpose-route data layer (arc 101 slice 03b). Reads `GET /v1/llm-routes` and
// drives the org / per-repo upsert + delete mutations over `apiFetch`. A "route"
// pins, for a PURPOSE on a SCOPE (the whole account, or one repo), which
// key/provider/model the pool must use. Mirrors `lib/keys.ts`: round-trip over
// `apiFetch` reusing the dev session bearer; `activeAccountId()` keys the read so
// an org switch refetches. The Agents page renders whatever comes back.

import type { LlmRoutePurposeAny, ReasoningEffort } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type RouteScope = 'org' | 'repo';

/** One route as the API serializes it — the frozen `/v1/llm-routes` contract. */
export type RouteWire = {
  readonly id: string;
  readonly scope: RouteScope;
  /** The scope's key: the repo id for a repo route; the account id for an org route. */
  readonly scope_ref: string;
  readonly purpose: string;
  readonly key_label: string | null;
  readonly provider: string | null;
  readonly model: string | null;
  /** How hard the pinned model thinks before acting; null = none requested. */
  readonly reasoning_effort: ReasoningEffort | null;
  readonly updated_at_iso: string;
};

/**
 * PUT body — every field optional, but the server 400s an EMPTY one: a route
 * has to pin at least one of key/provider/model. `nextRouteRow`'s `storable`
 * rule mirrors that client-side (`packages/domain/src/value-objects/route-row.ts`):
 * it returns a DELETE, never an empty PUT, once no pin axis survives a change.
 */
export type RouteBody = {
  readonly key_label?: string;
  readonly provider?: string;
  readonly model?: string;
  /**
   * Not a pin axis. Missing `provider` or `model`, or a pairing the model cannot
   * take, is a 422 (`reasoning_refused`) whose `detail` names why; only a value
   * outside the vocabulary is a 400. `null` = none; the
   * PUT replaces the row, so an omitted or null effort clears a stored one.
   */
  readonly reasoning_effort?: ReasoningEffort | null;
  /**
   * The version this write was built from (#5208): the route's `updated_at_iso`,
   * or `{absent: true}` when the caller read no route. A row that moved since is
   * a 409 `config-stale` naming who saved first. Omitted = the unconditional
   * upsert (the Agents page's "Apply mine anyway").
   */
  readonly expect?: RouteExpect;
};

/** A PUT's precondition — `SetRouteBodySchema.expect` in `apps/api/src/routes/llm-routes.ts`. */
export type RouteExpect = { readonly updated_at_iso: string } | { readonly absent: true };

export const LLM_ROUTES_QUERY_KEY = 'llm_routes';

/**
 * The one cache key `useLlmRoutes` reads under — exported so a write that patches
 * the list optimistically (`lib/agent-model-save.ts`) patches the SAME entry the
 * page renders, never a look-alike key that is silently ignored. `account` defaults to
 * the live org; a write outliving an org switch passes the org it was made in.
 */
export const llmRoutesQueryKey = (
  token: string | null,
  account: string | null = activeAccountId(),
) => [LLM_ROUTES_QUERY_KEY, token, account] as const;

/**
 * `GET /v1/llm-routes`, raw — every route the caller can see, org and repo scope
 * both. `useLlmRoutes`'s `queryFn` (below), and `repo-route-save.ts`'s round-5
 * replay re-read, share this ONE implementation: a replay must read the row
 * fresh, not through `refetch()`, whose default `cancelRefetch: true` can hand
 * back a cancelled read's stale cached rows (`isError: false`) rather than
 * rejecting — the exact race #5208 exists to refuse, reopened one layer up.
 */
export async function fetchLlmRoutes(token: string | null): Promise<readonly RouteWire[]> {
  return (await apiFetch<Page<RouteWire>>('/v1/llm-routes', token)).data;
}

/**
 * The caller's routes. Idle (no fetch) without a session, like `useKeys`.
 *
 * The wire is the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null: a purpose route is hand-authored operator config served whole, so
 * this is a plain query rather than `usePagedQuery`, and no Load-more is offered.
 */
export function useLlmRoutes(token: string | null = SESSION_TOKEN) {
  return createQuery<readonly RouteWire[]>(() => ({
    queryKey: llmRoutesQueryKey(token),
    enabled: token !== null,
    queryFn: () => fetchLlmRoutes(token),
  }));
}

/** Pin (or replace) the account-wide route for `purpose`. Returns the saved row. */
export async function upsertOrgRoute(
  purpose: LlmRoutePurposeAny,
  body: RouteBody,
  token: string | null = SESSION_TOKEN,
): Promise<RouteWire> {
  const { route } = await apiFetch<{ route: RouteWire }>(
    `/v1/llm-routes/org/${encodeURIComponent(purpose)}`,
    token,
    { method: 'PUT', body: JSON.stringify(body) },
  );
  return route;
}

/** Pin (or replace) `repoId`'s route for `purpose`. Returns the saved row. */
export async function upsertRepoRoute(
  repoId: string,
  purpose: LlmRoutePurposeAny,
  body: RouteBody,
  token: string | null = SESSION_TOKEN,
): Promise<RouteWire> {
  const { route } = await apiFetch<{ route: RouteWire }>(
    `/v1/llm-routes/repo/${encodeURIComponent(repoId)}/${encodeURIComponent(purpose)}`,
    token,
    { method: 'PUT', body: JSON.stringify(body) },
  );
  return route;
}

/**
 * Drop a route (204). Branches on scope so callers pass the row, not a path.
 * `expectUpdatedAt` is the DELETE's precondition (`ClearRouteQuerySchema`, a
 * query parameter — a DELETE has no body): the `updated_at_iso` the caller read,
 * refused 409 when the route changed since. Omitted = the unconditional clear.
 */
export async function deleteRoute(
  route: Pick<RouteWire, 'scope' | 'scope_ref' | 'purpose'>,
  token: string | null = SESSION_TOKEN,
  expectUpdatedAt?: string,
): Promise<void> {
  const purpose = encodeURIComponent(route.purpose);
  const path =
    route.scope === 'repo'
      ? `/v1/llm-routes/repo/${encodeURIComponent(route.scope_ref)}/${purpose}`
      : `/v1/llm-routes/org/${purpose}`;
  const query =
    expectUpdatedAt === undefined
      ? ''
      : `?expect_updated_at=${encodeURIComponent(expectUpdatedAt)}`;
  await apiFetch<void>(`${path}${query}`, token, { method: 'DELETE' });
}
